import { useCallback, useMemo, useState } from 'react';

import { usePericlesClient } from '../core/PericlesProvider';

import { buildEntityPathSegment } from './entityPath';

export interface UseEntityDeleteParams {
  entity: string;
  id?: string | number;
  onSuccess?: () => void;
  onError?: (err: unknown) => void;
}

export interface UseEntityDeleteResult {
  mutate: (idOverride?: string | number) => Promise<void>;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

function buildApiBase(baseUrl: string): string {
  const trimmed = baseUrl.replace(/\/$/, '');

  if (!trimmed) {
    return '/api';
  }

  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
}

export function useEntityDelete({ entity, id, onSuccess, onError }: UseEntityDeleteParams): UseEntityDeleteResult {
  const { baseUrl, fetcher } = usePericlesClient();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const apiBase = useMemo(() => buildApiBase(baseUrl), [baseUrl]);

  const mutate = useCallback(
    async (idOverride?: string | number) => {
      const resolvedId = idOverride ?? id;

      if (resolvedId === undefined || resolvedId === null) {
        throw new Error('An id is required to delete an entity');
      }

      setIsLoading(true);
      setError(null);

      const url = `${apiBase}/entities/${buildEntityPathSegment(entity)}/${encodeURIComponent(String(resolvedId))}`;

      try {
        const response = await fetcher(url, { method: 'DELETE' });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        onSuccess?.();
      } catch (err) {
        setError(err);
        onError?.(err);
      } finally {
        setIsLoading(false);
      }
    },
    [apiBase, entity, fetcher, id, onError, onSuccess]
  );

  return {
    mutate,
    isLoading,
    isError: Boolean(error),
    error,
  };
}
